import { useTranslate } from '@repo/shared/hooks'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Checkbox,
  Col,
  Icon,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Row,
  Separator,
  Skeleton,
  Typography,
} from '@repo/ui/components'
import * as React from 'react'
import { PaymentType, ValueWithUnit, type InputCurrency } from '..'
import { globalConfig } from '../..'
import { OrderBoxPriceTimer } from './order-box.price-timer'
import { OrderBoxReferral } from './order-box.referral'
import type { ReferralData } from './order-box.referral.types'
import type { FlatOrderData, OrderBoxTab } from './order-box.types'
import type { UserOTCReferral } from '@repo/services'

interface OrderBoxDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tab: OrderBoxTab
  assetTitle: string
  assetId: string
  price: number
  amount: number
  total: number
  onConfirm: () => void
  isPriceValid: boolean
  isLoading?: boolean
  isPlacingOrder?: boolean
  onRefreshPrice?: (referral_code?: string | null) => void
  finalPayableAmount?: number
  onTimeExpired?: () => void
  flatOrderData?: FlatOrderData
  onReferralChange?: (data: ReferralData | null, callback: () => void) => void
  hasDefaultReferral?: boolean
  isManualReferral?: boolean
  isCrypto?: boolean
  targetScale?: number
  referal?: UserOTCReferral
}

export const OrderBoxDialog: React.FC<OrderBoxDialogProps> = (
  props: OrderBoxDialogProps,
) => {
  const {
    open,
    onOpenChange,
    tab,
    assetTitle,
    assetId,
    price,
    // amount,
    // total,
    onConfirm,
    isPriceValid,
    isLoading,
    isPlacingOrder,
    onRefreshPrice,
    onTimeExpired,
    // finalPayableAmount,
    flatOrderData,
    onReferralChange,
    hasDefaultReferral,
    isManualReferral,
    isCrypto = false,
    targetScale = 1,
    referal,
  } = props

  const { t } = useTranslate()
  const isBuy = tab === 'buy'
  const [contractTerms, setContractTerms] = React.useState(false)

  const handleReferralChange = (
    data: ReferralData | null,
    callback: () => void,
  ) => {
    onReferralChange?.(data, callback)
  }

  const handleTimeExpired = () => {
    // Handle time expiration if needed
    onTimeExpired?.()
    console.log('Price validity expired')
  }

  const amount = flatOrderData?.targetAmount ?? 0
  const fee = flatOrderData?.otcFee ?? 0
  const feeCurrency = flatOrderData?.otcFeeCurrency ?? 'IRR'
  const total = flatOrderData?.baseAmount ?? 0
  const shouldHideFee = !isBuy && assetId === 'MALEK'
  const totalWithFee = shouldHideFee ? total : isBuy ? total + fee : total - fee

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent className='md:max-w-[480px] !px-0'>
        <ModalHeader>
          <ModalTitle className='text-sm !font-regular'>
            {isBuy ? t('order_buy_token') : t('order_sell_token')}
          </ModalTitle>
        </ModalHeader>
        <Col className='px-md gap-md overflow-y-auto'>
          <Typography size='md' weight='bold' className='text-center'>
            {assetId === 'MALEK' ? t('common_token_name') : assetTitle}
          </Typography>
          {/* Show default referral info only when using default referral (not manual) */}
          {referal && !referal.dismissable && (
            <Row className='items-center justify-between gap-md py-md'>
              <Row className='items-center gap-sm'>
                <Avatar className='h-10 w-10'>
                  <AvatarImage
                    src={
                      referal.photo
                        ? `${globalConfig.baseImageUrl}${referal.photo}`
                        : '/images/placeholder.webp'
                    }
                  />
                  <AvatarFallback>
                    {referal.name?.slice(0, 1).toUpperCase().slice(0, 2) || 'C'}
                  </AvatarFallback>
                </Avatar>
                <Col className='gap-2xs'>
                  <Typography size='2xs' color='text-neutral-surface-60'>
                    {t('order_consultant_your')}
                  </Typography>
                  <Typography size='xs' weight='bold'>
                    {referal.name}
                  </Typography>
                </Col>
              </Row>

              <Typography size='xs' className='whitespace-nowrap'>
                {flatOrderData?.agentDiscountPercentage}%{' '}
                {t('order_fee_discount')}
              </Typography>
            </Row>
          )}
          <Row className='justify-between'>
            <Typography size='sm' color='text-neutral-surface-80'>
              {t('property_id')}
            </Typography>
            <Typography size='sm' className='font-[AzarMehr]'>
              {assetId === 'MALEK' ? 'SQ²' : assetId}
            </Typography>
          </Row>
          <Row className='justify-between'>
            <Typography size='sm' color='text-neutral-surface-80'>
              {isBuy
                ? t('order_buy_price_per_token')
                : t('order_sell_price_per_token')}
            </Typography>
            <PaymentType value={price} />
          </Row>
          <Row className='justify-between'>
            <Typography size='sm' color='text-neutral-surface-80'>
              {isBuy ? t('common_amount') : t('order_amount_sell')}
            </Typography>
            {isPlacingOrder && (!amount || amount === 0) ? (
              <Skeleton className='h-5 w-20' />
            ) : (
              <ValueWithUnit
                value={amount / targetScale}
                unit={assetId === 'MALEK' || isCrypto ? 'crypto' : 'property'}
                scale={1}
                unitSize='sm'
                valueSize='sm'
              />
            )}
          </Row>
          <Row className='justify-between'>
            <Typography size='sm' color='text-neutral-surface-80'>
              {isBuy ? t('order_payable') : t('order_receive_final')}
            </Typography>
            {isPlacingOrder && (!total || total === 0) ? (
              <Skeleton className='h-5 w-24' />
            ) : (
              <PaymentType value={total} />
            )}
          </Row>
          {!shouldHideFee && (
            <Row className='justify-between'>
              <Typography size='sm' color='text-neutral-surface-80'>
                {t('order_fee')}
              </Typography>
              {isPlacingOrder && (!fee || fee === 0) ? (
                <Skeleton className='h-5 w-20' />
              ) : (
                <PaymentType
                  value={fee}
                  inputCurrency={feeCurrency as InputCurrency}
                />
              )}
            </Row>
          )}

          {/* Referral Section - Only show when no default referral */}
          <OrderBoxReferral
            assetTitle={assetTitle}
            flatOrderData={flatOrderData}
            onReferralChange={handleReferralChange}
            hasDefaultReferral={hasDefaultReferral}
            referal={referal}
          />

          {/* Price Validity Timer */}
          <Separator />
          {isPriceValid && (
            <OrderBoxPriceTimer
              onTimeExpired={handleTimeExpired}
              initialTime={30}
            />
          )}
          {!isPriceValid && (
            <Row className='justify-center'>
              <Button
                variant='ghost'
                color='negative'
                size='sm'
                onClick={() => onRefreshPrice?.(referal?.discountCode)}
              >
                {t('order_price_update_required')}
              </Button>
            </Row>
          )}

          {/* Final Payable Amount */}
          <Row className='justify-between'>
            <Typography size='sm' color='text-neutral-surface-80'>
              {t(
                isBuy ? 'order_final_buy_payable' : 'order_final_sell_payable',
              )}
            </Typography>
            {isPlacingOrder && (!totalWithFee || totalWithFee === 0) ? (
              <Skeleton className='h-6 w-24 my-2xs' />
            ) : (
              <Typography size='lg' weight='bold' className='text-primary-main'>
                <PaymentType value={totalWithFee} />
              </Typography>
            )}
          </Row>
        </Col>
        <Col className='p-md gap-xs'>
          <Row className='gap-md'>
            <Checkbox
              id='contractTerms'
              size='sm'
              checked={contractTerms}
              onCheckedChange={(checked) =>
                setContractTerms(checked as boolean)
              }
            />

            <Typography variant='label' size='sm'>
              {t('order_contract_terms')}
            </Typography>
          </Row>
          {!isPriceValid && !isLoading && !contractTerms && (
            <Row className='gap-md items-center -mr-[2px]'>
              <Icon name='AlertTriangle' color='fill-negative-surface-100' />
              <Typography
                variant='label'
                size='sm'
                color='text-negative-surface-100'
              >
                {t('order_contract_terms_error')}
              </Typography>
            </Row>
          )}
        </Col>
        <ModalFooter className='px-md'>
          <Button
            variant='fill'
            color={isBuy ? 'positive' : 'negative'}
            width='full'
            size='lg'
            onClick={onConfirm}
            disabled={!isPriceValid || isLoading || !contractTerms}
            loading={isLoading}
          >
            {isBuy ? t('order_buy') : t('order_sell')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
